import React, { useState, useEffect } from 'react';
import { MdPayments, MdSpeed } from 'react-icons/md';
import { FiCreditCard, FiX, FiAlertCircle, FiHeart, FiBookmark, FiSave, FiUser, FiPhone } from 'react-icons/fi';
import type { Service, PaymentServiceType, WalletCard } from '../../types';
import {
  COUNTER_REFERENCE_SERVICES,
} from '../../types/payment.types';
import { getServiceName, getServiceLogo, getServiceMimeType, getServiceEntity, getServiceCode, getServicePaymentPageId } from './helpers';
import { useServiceTypeConfig } from '../../hooks/useServiceTypeConfig';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ECARD_PAY_ENABLED } from '../../config/ecard';

export interface PaymentFormModalProps {
  isOpen: boolean;
  service: Service | null;
  walletCards: WalletCard[];
  prefillData?: {
    referencia?: string;
    entidade?: string;
    montante?: string;
  } | null;
  isFavorite?: boolean;
  categoryId?: string;
  categoryName?: string;
  loading?: boolean;
  onSubmit: (data: {
    referencia: string;
    montante: string;
    entidade?: string;
    useWallet: boolean;
    walletCardId?: string;
  }) => void;
  onClose: () => void;
  onToggleFavorite?: () => void;
  onSaveReference?: (label: string, referencia: string, entidade?: string) => void;
}

const PaymentFormModal: React.FC<PaymentFormModalProps> = ({
  isOpen,
  service,
  walletCards,
  prefillData,
  isFavorite = false,
  categoryId,
  categoryName,
  loading: paymentLoading = false,
  onSubmit,
  onClose,
  onToggleFavorite,
  onSaveReference,
}) => {
  const { cliente } = useAuth();
  const [referencia, setReferencia] = useState('');
  const [montante, setMontante] = useState('');
  const [entidade, setEntidade] = useState('');
  const [selectedWalletCard, setSelectedWalletCard] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSaveRef, setShowSaveRef] = useState(false);
  const [refLabel, setRefLabel] = useState('');
  const [useMeuNumero, setUseMeuNumero] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [useMinhaRefContador, setUseMinhaRefContador] = useState(false);

  // Reset form when modal opens, apply prefill data
  useEffect(() => {
    if (isOpen) {
      // Buscar telefone atualizado da API de perfil
      const localTel = cliente?.TELEMOVEL || '';
      if (!localTel) {
        api.get('/client/profile').then(resp => {
          const tel = resp.data?.data?.telefone || '';
          if (tel) {
            setUserPhone(tel);
            const svcCode = service ? getServiceCode(service) : '';
            const catNorm = (categoryName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const isCtr = catNorm.includes('contador');
            const isRcg = !isCtr && (svcCode === 'RCG' || catNorm.includes('recarga'));
            if (isRcg && !prefillData?.referencia) {
              setReferencia(tel);
              setUseMeuNumero(true);
            }
          }
        }).catch(() => {});
      } else {
        setUserPhone(localTel);
      }

      const svcCode = service ? getServiceCode(service) : '';
      const catNorm = (categoryName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const isCtr = catNorm.includes('contador');
      const isRcg = !isCtr && (svcCode === 'RCG' || catNorm.includes('recarga'));

      const preRef = prefillData?.referencia || (isRcg && localTel ? localTel : '');
      setReferencia(preRef);
      setUseMeuNumero(isRcg && !!localTel && !prefillData?.referencia);
      setUseMinhaRefContador(false);
      setMontante(prefillData?.montante || '');
      setEntidade(prefillData?.entidade || '');
      setSelectedWalletCard('');
      setErrors({});
      setShowSaveRef(false);
      setRefLabel('');
    }
  }, [isOpen, prefillData, service, categoryName, cliente]);

  const { getConfig } = useServiceTypeConfig();

  if (!isOpen || !service) return null;

  const serviceType = (getServiceCode(service) as PaymentServiceType) || 'WBS';
  const config = getConfig(serviceType);

  // Pacotes vêm dinamicamente do backend (pg_template_pacote) via service.pacotes
  const servicePacotes: Array<{ valor: number; label: string }> = (service as any)?.pacotes || [];
  const packageConfig = servicePacotes.length > 0
    ? { title: 'Escolha o pacote', options: servicePacotes.map(p => ({ value: String(p.valor), label: p.label })) }
    : null;

  const normalizedCatName = (categoryName || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const isCounterCategory = normalizedCatName.includes('contador');
  const needsCounterLookup = isCounterCategory || COUNTER_REFERENCE_SERVICES.includes(
    getServicePaymentPageId(service) || ''
  );
  const isRecarga = !isCounterCategory && (serviceType === 'RCG' || normalizedCatName.includes('recarga'));

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!referencia.trim()) newErrors.referencia = 'Campo obrigatório';
    if (!montante.trim() || parseFloat(montante) <= 0) newErrors.montante = 'Insira um valor válido';
    if (config.showEntityField && !entidade.trim()) newErrors.entidade = 'Campo obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        referencia,
        montante,
        entidade: config.showEntityField ? entidade : undefined,
        useWallet: !!selectedWalletCard,
        walletCardId: selectedWalletCard || undefined,
      });
    }
  };

  const logo = getServiceLogo(service);
  const mimeType = getServiceMimeType(service);

  const handleSaveReference = () => {
    if (refLabel.trim() && referencia.trim() && onSaveReference) {
      onSaveReference(refLabel.trim(), referencia.trim(), entidade || undefined);
      setShowSaveRef(false);
      setRefLabel('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="sticky top-0 bg-white flex items-center justify-between p-4 border-b border-pagali-gray-200">
          <h3 className="text-lg font-semibold text-pagali-gray-800">Pagamento</h3>
          <div className="flex items-center gap-2">
            {onToggleFavorite && (
              <button
                onClick={onToggleFavorite}
                className={`p-2 rounded-lg transition-colors ${
                  isFavorite
                    ? 'text-pagali-error bg-red-50 hover:bg-red-100'
                    : 'text-pagali-gray-400 hover:bg-pagali-gray-100 hover:text-pagali-error'
                }`}
                title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
              >
                <FiHeart className={`text-xl ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-pagali-gray-100 text-pagali-gray-500 transition-colors"
            >
              <FiX className="text-xl" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Service Info */}
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-pagali-primary/10 to-pagali-purple/10 rounded-xl">
            {logo ? (
              <img
                src={`data:${mimeType};base64,${logo}`}
                alt={getServiceName(service)}
                className="w-16 h-16 rounded-xl object-contain bg-white p-2"
              />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center">
                <MdPayments className="text-2xl text-pagali-primary" />
              </div>
            )}
            <div>
              <h4 className="font-semibold text-pagali-gray-800">{getServiceName(service)}</h4>
              <p className="text-sm text-pagali-gray-500">{getServiceEntity(service)}</p>
            </div>
          </div>

          {/* Counter Reference Link */}
          {needsCounterLookup && (
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
              <a
                href="https://entidadeservico.vinti4.cv/prepago"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 group"
              >
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FiAlertCircle className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Não sabe a referência?</p>
                  <p className="text-xs text-gray-500 group-hover:text-blue-600">Consulte o seu contador no portal Vinti4 →</p>
                </div>
              </a>
            </div>
          )}

          {/* Entity Field */}
          {config.showEntityField && (
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
                Código da Entidade
              </label>
              <input
                type="text"
                value={entidade}
                onChange={(e) => setEntidade(e.target.value)}
                placeholder="Insira o código da entidade"
                className={`input-field ${errors.entidade ? 'border-pagali-error' : ''}`}
              />
              {errors.entidade && (
                <p className="text-pagali-error text-xs mt-1">{errors.entidade}</p>
              )}
            </div>
          )}

          {/* My Number Toggle (Recharge only, not counters) */}
          {isRecarga && (
            <div className="flex items-center gap-3 p-3 bg-pagali-gray-50 rounded-xl">
              <FiPhone className="text-pagali-primary flex-shrink-0" />
              <span className="text-sm text-pagali-gray-700 flex-1">Meu numero</span>
              <button
                type="button"
                onClick={() => {
                  const next = !useMeuNumero;
                  setUseMeuNumero(next);
                  if (next) {
                    if (userPhone) {
                      setReferencia(userPhone);
                    } else {
                      setUseMeuNumero(false);
                      setErrors({ ...errors, referencia: 'Numero nao encontrado. Atualize o seu perfil para adicionar o seu telemovel.' });
                    }
                  } else {
                    setReferencia('');
                  }
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${useMeuNumero ? 'bg-pagali-primary' : 'bg-pagali-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${useMeuNumero ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          )}

          {/* My Counter Reference Toggle (Counter categories only) */}
          {isCounterCategory && (
            <div className="flex items-center gap-3 p-3 bg-pagali-gray-50 rounded-xl">
              <MdSpeed className="text-pagali-primary flex-shrink-0" />
              <span className="text-sm text-pagali-gray-700 flex-1">Minha Referência</span>
              <button
                type="button"
                onClick={() => {
                  const savedRef = localStorage.getItem('contador_prepago_ref') || '';
                  if (!savedRef) {
                    setErrors({ ...errors, referencia: 'Referência não configurada. Aceda ao perfil para adicionar.' });
                    return;
                  }
                  const next = !useMinhaRefContador;
                  setUseMinhaRefContador(next);
                  if (next) {
                    setReferencia(savedRef);
                  } else {
                    setReferencia('');
                  }
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${useMinhaRefContador ? 'bg-pagali-primary' : 'bg-pagali-gray-300'}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${useMinhaRefContador ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          )}

          {/* Reference Field */}
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
              {isCounterCategory ? 'Referência do Contador' : isRecarga ? 'Numero de telemovel' : config.referenceLabel}
            </label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => {
                setReferencia(e.target.value);
                if (useMeuNumero) setUseMeuNumero(false);
                if (useMinhaRefContador) setUseMinhaRefContador(false);
              }}
              placeholder={isCounterCategory ? 'Insira a referência do contador' : isRecarga ? 'Numero de telemovel' : config.referenceHint}
              className={`input-field ${errors.referencia ? 'border-pagali-error' : ''}`}
              readOnly={(useMeuNumero && !!userPhone) || useMinhaRefContador}
            />
            {errors.referencia && (
              <p className="text-pagali-error text-xs mt-1">{errors.referencia}</p>
            )}
          </div>

          {/* Amount Field */}
          <div>
            <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
              Montante (CVE)
            </label>
            {packageConfig ? (
              <select
                value={montante}
                onChange={(e) => setMontante(e.target.value)}
                className={`input-field ${errors.montante ? 'border-pagali-error' : ''}`}
              >
                <option value="">{packageConfig.title}</option>
                {packageConfig.options.map((opt: { value: string; label: string }) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="number"
                value={montante}
                onChange={(e) => setMontante(e.target.value)}
                placeholder="0"
                min="0"
                className={`input-field ${errors.montante ? 'border-pagali-error' : ''}`}
              />
            )}
            {errors.montante && (
              <p className="text-pagali-error text-xs mt-1">{errors.montante}</p>
            )}
          </div>

          {/* Wallet Selection — eCard SISP, só onde o TokenPayment está activo */}
          {ECARD_PAY_ENABLED && walletCards.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-pagali-gray-700 mb-2">
                Método de pagamento
              </label>
              <div className="space-y-2">
                {walletCards.map((card) => (
                  <button
                    key={card.ID || card.id}
                    type="button"
                    onClick={() => setSelectedWalletCard(
                      selectedWalletCard === (card.ID || card.id) ? '' : (card.ID || card.id || '')
                    )}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all ${
                      selectedWalletCard === (card.ID || card.id)
                        ? 'border-pagali-primary bg-pagali-primary/5'
                        : 'border-pagali-gray-200 hover:border-pagali-gray-300'
                    }`}
                  >
                    <p className="text-sm font-medium text-pagali-gray-800">
                      {card.DESIG || card.designacao || 'Wallet Card'}
                    </p>
                    <p className="text-xs text-pagali-gray-500">
                      {card.designacao || (card.NUMERO ? `•••• ${card.NUMERO.slice(-4)}` : '')}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Save Reference Option */}
          {onSaveReference && referencia.trim() && (
            <div className="border-t border-pagali-gray-100 pt-4">
              {showSaveRef ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-pagali-gray-700">
                    Nome para esta referência
                  </label>
                  <input
                    type="text"
                    value={refLabel}
                    onChange={(e) => setRefLabel(e.target.value)}
                    placeholder="Ex: Casa, Escritório, Mãe..."
                    className="input-field"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveReference}
                      disabled={!refLabel.trim()}
                      className="flex-1 btn-primary py-2 text-sm disabled:opacity-50"
                    >
                      <FiSave className="inline mr-1" /> Guardar
                    </button>
                    <button
                      onClick={() => {
                        setShowSaveRef(false);
                        setRefLabel('');
                      }}
                      className="px-4 py-2 text-sm text-pagali-gray-600 hover:bg-pagali-gray-100 rounded-lg"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowSaveRef(true)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-pagali-primary hover:bg-pagali-primary/5 rounded-lg transition-colors"
                >
                  <FiBookmark />
                  Guardar esta referência
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white p-4 border-t border-pagali-gray-200">
          <button
            onClick={handleSubmit}
            disabled={paymentLoading}
            className="w-full btn-primary flex items-center justify-center gap-2 py-3 disabled:opacity-50"
          >
            {paymentLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                A processar...
              </>
            ) : (
              <>
                <FiCreditCard />
                {selectedWalletCard ? 'Pagar com Pagali Card' : 'Pagar com Cartão'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentFormModal;
